import React, { useMemo, useState } from 'react';

import dayjs from '~/timeInit';

import AddIcon from '@mui/icons-material/Add';
import { Alert, Box, Button, CircularProgress, Divider, MenuItem, Stack, TextField, Typography } from '@mui/material';

import { CostPriceConversion, useGetConvertedCostPricesInAud } from '~/hooks/apis/fx/useGetConvertedCostPricesInAud';
import { useGetDeparturesInventory } from '~/hooks/apis/tours/useTourInventory';

import { FixedByDepartureCostPriceItem, SlidingScaleByDeparturePricingItem } from '~/types/tours/costPrice';
import { CostPriceKind } from '~/types/tours/supplier';

import CostBreakdown from './CostBreakdown';
import DepartureInventoryPanel from './DepartureInventoryPanel';
import FixedPriceTable from './FixedPriceTable';
import SlidingScaleTable from './SlidingScaleTable';
import { buildScaleAnnotation, fixedPricingAsScale, summariseDepartureCost } from './departureInventory';
import { DepartureItem, DepartureRow, groupDeparturesForSelector, hasPrice } from './departureRows';
import { icsProfitShare, withProfitShare } from './icsProfitShare';
import useInventorySnapshot from './useInventorySnapshot';

interface Props {
  rows: Array<DepartureRow<DepartureItem>>;
  heading: string;
  /** How the service is costed, which decides what a departure's prices look like. */
  kind: CostPriceKind;
  /** The service being priced, which inventory is recorded against. */
  serviceId: string;
  /** The service's supplier in Salesforce, which decides whether ICS's profit share applies. */
  sourceSupplierId?: string | null;
  /** Opens the upload for a departure, or blank to price several at once. */
  onUpload: (departure: string | null) => void;
  /** The tour's own offer, so an LTLE's travellers can be told apart. */
  tourOfferId?: string;
  /** Shown in place of the inventory, as for an LTLE whose departures are shared with its base tour. */
  inventoryNote?: React.ReactNode;
}

function formatDate(date?: string): string {
  return date ? dayjs(date).format('D MMM YYYY') : 'Not on this tour';
}

/** One shared priced row per date/season for `All`, or the selected date/season. */
export function visibleDeparturePriceRows<T extends DepartureItem>(
  rows: Array<DepartureRow<T>>,
  selectedKey: string,
): Array<DepartureRow<T>> {
  const groups = groupDeparturesForSelector(rows);
  const visibleGroups = selectedKey ? groups.filter(([key]) => key === selectedKey) : groups;

  return visibleGroups.flatMap(([, groupRows]) => {
    const pricedRows = groupRows.filter(hasPrice);
    const first = pricedRows[0];

    return first ? [{ ...first, items: pricedRows.flatMap((row) => row.items) }] : [];
  });
}

export function departurePriceRowsForSeason<T extends DepartureItem>(
  rows: Array<DepartureRow<T>>,
  season?: string,
): Array<DepartureRow<T>> {
  if (!season) {
    return rows;
  }

  return rows.map((row) => ({
    ...row,
    items: row.items.filter((item) => item.season.toLowerCase() === season.toLowerCase()),
  }));
}

/** Departure selection followed by the matching cost prices and inventory. */
export default function DepartureCostPrices({
  rows,
  heading,
  kind,
  serviceId,
  sourceSupplierId,
  onUpload,
  tourOfferId,
  inventoryNote,
}: Props) {
  const [selectedKey, setSelectedKey] = useState('');

  const departureGroups = useMemo(() => groupDeparturesForSelector<DepartureRow<DepartureItem>>(rows), [rows]);

  const selectedRows = useMemo(
    () => departureGroups.find(([key]) => key === selectedKey)?.[1] ?? [],
    [departureGroups, selectedKey],
  );
  const selected = selectedRows[0];

  const unknown = rows.filter((row) => !row.isKnownDeparture);

  /**
   * Inventory is recorded against real departures, so a price stored against a
   * departure the tour does not run has none to fetch.
   */
  const {
    data: inventory,
    isLoading: isLoadingInventory,
    error: inventoryError,
  } = useGetDeparturesInventory({
    serviceId,
    packageDepartureIds: selectedRows.flatMap((row) => row.packageDepartureId ?? []),
  });

  const visiblePriceRows = useMemo(() => {
    const visible = visibleDeparturePriceRows(rows, selectedKey);
    if (!selectedKey) {
      return visible;
    }

    const rowSeasons = Array.from(new Set(selectedRows.flatMap((row) => row.seasons)));
    const season = inventory?.season ?? (rowSeasons.length === 1 ? rowSeasons[0] : undefined);
    return season ? departurePriceRowsForSeason(visible, season) : [];
  }, [inventory?.season, rows, selectedKey, selectedRows]);
  const selectedPriceRow = selectedKey ? visiblePriceRows[0] : undefined;

  const { snapshot, date: snapshotDate, onDateChange } = useInventorySnapshot(inventory);
  const inventoryPricing = useMemo(() => {
    if (kind === 'SlidingScaleByDeparture' || kind === 'FixedPerGroupByDeparture') {
      return (selectedPriceRow?.items ?? []) as Array<SlidingScaleByDeparturePricingItem>;
    }

    const allocated = inventory?.entries.filter((entry) => entry.operation === 'allocated').length ?? 1;
    return fixedPricingAsScale((selectedPriceRow?.items ?? []) as Array<FixedByDepartureCostPriceItem>, allocated);
  }, [inventory?.entries, kind, selectedPriceRow?.items]);
  const costSummary = useMemo(() => summariseDepartureCost(snapshot, inventoryPricing), [inventoryPricing, snapshot]);
  const profitShare = icsProfitShare(sourceSupplierId, costSummary.totalCost);
  const costPriceToConvert = useMemo<Array<CostPriceConversion>>(
    () =>
      selected
        ? [
            {
              serviceId,
              amount: withProfitShare(costSummary.totalCost, profitShare),
              currency: costSummary.currency,
            },
          ]
        : [],
    [costSummary.currency, costSummary.totalCost, profitShare, selected, serviceId],
  );
  const { convertedCostsByService, isLoadingByService } = useGetConvertedCostPricesInAud(costPriceToConvert);
  const convertedCost = convertedCostsByService[serviceId];
  const isConvertingCost = isLoadingByService[serviceId];

  if (!rows.length) {
    return (
      <Alert
        severity="info"
        action={
          <Button color="inherit" size="small" startIcon={<AddIcon />} onClick={() => onUpload(null)}>
            Add prices
          </Button>
        }
      >
        This tour has no departures to price yet.
      </Alert>
    );
  }

  const priced = selectedRows.some(hasPrice);

  return (
    <Stack spacing={2} sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField
          select
          label="Departure"
          value={selectedKey}
          onChange={(event) => setSelectedKey(event.target.value)}
          sx={{ minWidth: 420 }}
          InputLabelProps={{ shrink: true }}
          SelectProps={{
            MenuProps: { PaperProps: { sx: { maxHeight: 420 } } },
            displayEmpty: true,
            renderValue: (value) => {
              const row = departureGroups.find(([key]) => key === value)?.[1][0];

              return row ? `${row.departureName} · ${formatDate(row.departureDate)}` : 'All';
            },
          }}
        >
          <MenuItem value="">All</MenuItem>

          {departureGroups.map(([groupKey, groupRows]) => {
            const row = groupRows[0];
            const groupHasPrice = groupRows.some(hasPrice);

            return (
              <MenuItem key={groupKey} value={groupKey}>
                <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ width: '100%' }}>
                  <Typography variant="body2" sx={{ minWidth: 116 }}>
                    {row.departureName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(row.departureDate)}
                  </Typography>
                  <Box flex={1} />
                  {!groupHasPrice && (
                    <Typography variant="caption" color="warning.main">
                      No price
                    </Typography>
                  )}
                </Stack>
              </MenuItem>
            );
          })}
        </TextField>

        <Button variant="contained" onClick={() => onUpload(selected?.departure ?? null)}>
          {selected ? (priced ? 'Edit prices' : 'Add prices') : 'Upload prices'}
        </Button>
      </Stack>

      {!!selected && (
        <Stack spacing={1}>
          <Typography variant="h4" fontWeight={600}>
            Cost summary
          </Typography>
          {isLoadingInventory ? (
            <CircularProgress size={28} />
          ) : (
            <CostBreakdown
              travellers={costSummary}
              suppliers={[
                {
                  key: 'service',
                  summary: costSummary,
                  profitShare,
                  audCost: convertedCost,
                  isConverting: isConvertingCost,
                },
              ]}
            />
          )}
        </Stack>
      )}

      <Typography variant="h4" fontWeight={600}>
        {heading}
      </Typography>

      {visiblePriceRows.length ? (
        visiblePriceRows.map((row) => (
          <Stack key={row.key} spacing={1} sx={{ minWidth: 0 }}>
            <Typography variant="h6">{row.departureName}</Typography>
            {kind === 'FixedByDeparture' ? (
              <FixedPriceTable prices={row.items as Array<FixedByDepartureCostPriceItem>} />
            ) : (
              <SlidingScaleTable
                showSingleSupplement={kind !== 'FixedPerGroupByDeparture'}
                pricing={row.items as Array<SlidingScaleByDeparturePricingItem>}
                annotation={
                  row.key === selected?.key
                    ? buildScaleAnnotation(
                        snapshot,
                        row.departureName,
                        row.items as Array<SlidingScaleByDeparturePricingItem>,
                        tourOfferId,
                      )
                    : undefined
                }
              />
            )}
          </Stack>
        ))
      ) : !selected ? (
        <Alert severity="info">No cost prices recorded against this service yet.</Alert>
      ) : null}

      {!!unknown.length && (
        <Alert severity="error">
          {unknown.length} price{unknown.length > 1 ? 's are' : ' is'} stored against a departure this tour does not
          run, so nothing will resolve against {unknown.length > 1 ? 'them' : 'it'}:{' '}
          {unknown.map((row) => row.departureName).join(', ')}.
        </Alert>
      )}

      {!!selected && !priced && (
        <Alert severity="info">
          {selected.departureName} has no cost price yet
          {selected.departureDate ? `, and leaves ${formatDate(selected.departureDate)}` : ''}.
        </Alert>
      )}

      {/* Inventory overlays the scale, so it is shown where a scale exists.
          A fixed rate has no bands for a departure's pax count to sit in. */}
      {!!selected && selected.isKnownDeparture && (
        <>
          <Divider />

          <Stack spacing={1} sx={{ minWidth: 0 }}>
            <Typography variant="h4" fontWeight={600}>
              Inventory
            </Typography>

            {inventoryNote ?? (
              <DepartureInventoryPanel
                inventory={inventory}
                snapshotDate={snapshotDate}
                onDateChange={onDateChange}
                items={inventoryPricing}
                isLoading={isLoadingInventory}
                error={inventoryError}
                showCosts={false}
                showCostChart
                tourOfferId={tourOfferId}
              />
            )}
          </Stack>
        </>
      )}
    </Stack>
  );
}
